/*!
 * speakers.js — speakers(): more than two outputs, and where a sound is
 * © 2026 Rob Canning — GPLv3
 *
 *     speakers(quad)                    4 outputs: 1 FL  2 FR  3 RR  4 RL
 *     speakers(octo)  speakers(dome8)  speakers(cube24)
 *     speakers(azi:[30, -30, -110, 110])            a ring of your own, in output order
 *     speakers(azi:[45, -45, 45, -45], elev:[0, 0, 45, 45])
 *     speakers(stereo)                  back to two
 *     speakers()                        says what there is
 *
 *     synth(uid:a, azi:90)              at the left (azimuth positive to the left)
 *     synth(uid:a, azi:follow(src1.azi), dist:follow(src1.dist))
 *     audio(src:rain.flac, azi:sinOsc(0.05, 0, -180, 180))
 *
 * The layouts are the SuperCollider receiver's (speakerGains.js): a piece
 * written for it plays from the browser on the same speakers. pan: keeps
 * to outputs 1 and 2, as on a stereo output; a sound with azi: is panned
 * over all of them. A layout the output has too few channels for folds
 * down to stereo, so a quad piece can be rehearsed on headphones.
 *
 * On Linux, Chrome and Electron offer as many channels as PipeWire's default
 * has (pulse.default.position in pipewire-pulse.conf), whatever the
 * interface has: speakers() says when that is the catch.
 */

import { LAYOUTS, prepareLayout, speakerGains } from "./speakerGains.js";
import { bindParam, isMoving, isOscRef } from "../../control/paramBinding.js";
import { isModulator } from "./modShapes.js";
import { evalMaybeRandom } from "./patterns.js";
import { onReset } from "../../system/teardown.js";

export { LAYOUTS };

let ctxRef = null;
let layout = prepareLayout(LAYOUTS.stereo);
let layoutName = "stereo";
let width = 2;                   // the channels the chain runs at
const stages = new Set();        // every widened node in the chain (wideStage)
const panners = new Set();       // every sound's panner (spatialPanner)

/** The channels everything runs at: 2, or the layout's when the output has them. */
export const outputWidth = () => width;

/** fn(width) each time that changes: what is built at a width (an fx() bus) builds itself again. */
export function onWidth(fn) { widthListeners.add(fn); return () => widthListeners.delete(fn); }
const widthListeners = new Set();

/**
 * A two-channel node (a compressor, a stereo panner) in a chain that may
 * be wider: channels 1-2 go through it, the others two by two through a
 * copy of it (makePair) or past it (no makePair).
 */
export function wideStage(ctx, node, makePair = null) {
  const input = ctx.createGain(), output = ctx.createGain();
  const stage = { input, output, node, parts: [] };
  stage.setWidth = w => {
    // exactly the chain's channels in, each feed mixed to them by itself: a
    // feedback loop left wider by a sound gone (the bus delay's) runs on
    // silent, and summed first it took the chain down 6 dB going to stereo
    fitTo(input, w);
    input.disconnect();
    for (const n of stage.parts) { try { n.disconnect(); } catch { /* ignore */ } }
    try { node.disconnect(); } catch { /* ignore */ }
    stage.parts = [];
    if (w <= 2) { input.connect(node).connect(output); return; }
    const split = new ChannelSplitterNode(ctx, { numberOfOutputs: w });
    const merge = new ChannelMergerNode(ctx, { numberOfInputs: w });
    stage.parts.push(split, merge);
    input.connect(split);
    for (let c = 0; c < w; c += 2) {
      const inner = c === 0 ? node : makePair?.();
      if (!inner) {                                       // past it
        split.connect(merge, c, c);
        if (c + 1 < w) split.connect(merge, c + 1, c + 1);
        continue;
      }
      const pairIn = new ChannelMergerNode(ctx, { numberOfInputs: 2 });
      const pairOut = new ChannelSplitterNode(ctx, { numberOfOutputs: 2 });
      split.connect(pairIn, c, 0);
      if (c + 1 < w) split.connect(pairIn, c + 1, 1);
      pairIn.connect(inner).connect(pairOut);
      pairOut.connect(merge, 0, c);
      if (c + 1 < w) pairOut.connect(merge, 1, c + 1);
      stage.parts.push(pairIn, pairOut);
      if (inner !== node) stage.parts.push(inner);
    }
    merge.connect(output);
  };
  stage.setWidth(width);
  stages.add(stage);
  return stage;
}

/** A node made to take exactly w channels, mixed as the chain is. */
function fitTo(node, w) {
  node.channelCountMode = "explicit";
  node.channelCount = Math.max(2, w);
  node.channelInterpretation = w > 2 ? "discrete" : "speakers";
}

/** The way into an fx() bus: as wide as the chain, kept so (as wideStage's). */
export function widthInput(ctx) {
  const node = ctx.createGain();
  fitTo(node, width);
  fitted.add(new WeakRef(node));
  return node;
}
const fitted = new Set();         // those inputs, while their buses last

/** A compressor's copy, for another pair of channels. */
export function copyCompressor(ctx, c) {
  const k = ctx.createDynamicsCompressor();
  for (const p of ["threshold", "knee", "ratio", "attack", "release"]) k[p].value = c[p].value;
  return k;
}

/**
 * What a sound goes through on its way to a bus when it has no azi: mono
 * made two (both sides, as a stereo chain always did), two left as they
 * are, a file of more channels kept to its own as far as the chain is wide
 * -- so in a wider chain it is heard on outputs 1 and 2, not on 1 alone,
 * and nothing wider than the chain reaches it (a quad sound summed into a
 * stereo bus would take the other sounds there down 6 dB with it).
 */
export function toBus(ctx, channels = 2) {
  const node = new GainNode(ctx, { channelCountMode: "explicit", channelInterpretation: "speakers" });
  node._fit = () => { node.channelCount = Math.max(2, Math.min(channels, width)); };
  node._fit();
  if (channels > 2) wideExits.add(new WeakRef(node));
  return node;
}
const wideExits = new Set();      // toBus nodes of more than two channels, while they last

/**
 * A sound's place among the speakers: in (any channels, summed to one), out
 * one channel per output -- as many as the chain has, made again when that
 * changes. set() moves it; dispose() lets it go.
 */
export function spatialPanner(ctx) {
  const input = new GainNode(ctx, { channelCount: 1, channelCountMode: "explicit", channelInterpretation: "speakers" });
  const output = ctx.createGain();
  let w = 0, merge = null, gains = [];
  const at = { azi: 0, elev: 0, dist: 1 };
  const p = {
    input, output, at,
    // glide: the time constant of the move, from `when` (a step's own time)
    set(next = {}, glide = 0.02, when = ctx.currentTime) {
      for (const k of SPATIAL_KEYS) if (Number.isFinite(next[k])) at[k] = next[k];
      const lay = w === layout.n ? layout : FOLD;
      const g = speakerGains(lay, at.azi, at.elev, at.dist);
      const t = Math.max(when, ctx.currentTime);
      gains.forEach((node, c) => {
        try { node.gain.setTargetAtTime(g[c] ?? 0, t, glide); } catch { /* ignore */ }
      });
    },
    // one gain per output, into a merger as wide as the chain
    build() {
      p.unbuild();
      w = width;
      merge = new ChannelMergerNode(ctx, { numberOfInputs: w });
      gains = [];
      for (let c = 0; c < w; c++) {
        const g = new GainNode(ctx, { gain: 0 });
        input.connect(g).connect(merge, 0, c);
        gains.push(g);
      }
      merge.connect(output);
      p.set({}, 0.001);
    },
    unbuild() {
      try { input.disconnect(); } catch { /* ignore */ }
      for (const g of gains) { try { g.disconnect(); } catch { /* ignore */ } }
      try { merge?.disconnect(); } catch { /* ignore */ }
      gains = []; merge = null;
    },
    dispose() {
      panners.delete(p);
      p.unbuild();
      try { output.disconnect(); } catch { /* ignore */ }
    },
  };
  p.build();
  panners.add(p);
  return p;
}

const FOLD = prepareLayout(LAYOUTS.stereo);

/** The sound's own azi:, elev:, dist: -- written on it, or not. */
export const SPATIAL_KEYS = ["azi", "elev", "dist"];
export const hasSpatial = params => !!params && SPATIAL_KEYS.some(k => params[k] != null);

// what a follow() of an o2p's .azi / .elev / .dist (0..1 on the bus) means
const FOLLOW_RANGE = { azi: [0, 360], elev: [-90, 90], dist: [0, 1] };

/**
 * A sound's azi:, elev:, dist: on its panner: a number (rand() drawn), or
 * a follow() / sinOsc() that moves it. A pattern is left to the caller
 * (a synth steps it). Returns the unbinds.
 */
export function bindSpatial(panner, params) {
  const unbinds = [];
  const now = {};
  for (const k of SPATIAL_KEYS) {
    const v = params?.[k];
    if (v == null || v?.type === "pattern") continue;
    if (isMoving(v) || isOscRef(v)) {
      // a sinOsc(..., lo, hi) is in degrees already; a follow() is 0..1
      const [min, max] = isModulator(v) ? [-Infinity, Infinity] : FOLLOW_RANGE[k];
      const b = bindParam(v, x => panner.set({ [k]: x }), { min, max, default: k === "dist" ? 1 : 0 });
      if (Number.isFinite(b.value)) now[k] = b.value;
      if (b.unbind) unbinds.push(b.unbind);
    } else {
      const x = Number(evalMaybeRandom(v));
      if (Number.isFinite(x)) now[k] = x;
    }
  }
  panner.set(now, 0.001);
  return unbinds;
}

// the context's output: as wide as the layout, if it has the channels
function applyWidth(w) {
  const dest = ctxRef?.destination;
  if (dest) {
    dest.channelCount = w;
    dest.channelCountMode = "explicit";
    dest.channelInterpretation = w > 2 ? "discrete" : "speakers";
  }
  if (w === width) return;
  width = w;
  for (const s of stages) s.setWidth(w);
  for (const r of fitted) { const n = r.deref(); if (n) fitTo(n, w); else fitted.delete(r); }
  for (const p of panners) p.build();
  for (const r of wideExits) { const n = r.deref(); if (n) n._fit(); else wideExits.delete(r); }
  for (const fn of widthListeners) { try { fn(w); } catch (e) { console.warn("[speakers]", e); } }
}

/** audioShared.js hands over the context once it has made it. */
export function attachContext(ctx) { ctxRef = ctx; }

/**
 * Choose the speakers: a layout's name, or azi:[...] (and elev:[...]) of
 * your own. Returns what it says.
 */
export function setSpeakers(nameOrDirs) {
  const dirs = Array.isArray(nameOrDirs) ? nameOrDirs : LAYOUTS[nameOrDirs];
  if (!dirs?.length) {
    return { ok: false, text: `speakers: no layout called ${nameOrDirs} -- ${Object.keys(LAYOUTS).join(", ")}, or azi:[...]` };
  }
  layoutName = Array.isArray(nameOrDirs) ? `${dirs.length} of your own` : nameOrDirs;
  layout = prepareLayout(dirs);
  const have = ctxRef?.destination?.maxChannelCount ?? 2;
  const fits = layout.n <= have;
  applyWidth(fits && layout.n > 2 ? layout.n : 2);
  for (const p of panners) p.set();       // the same width, a new layout
  return { ok: true, text: describe(), folded: !fits };
}

/** speakers() with nothing: the layout, and the outputs there are. */
export function describe() {
  const have = ctxRef?.destination?.maxChannelCount ?? 2;
  const head = `speakers: ${layoutName} (${layout.n}), the output has ${have} channel${have === 1 ? "" : "s"}`;
  if (layout.n <= 2) return head;
  if (layout.n <= have) return `${head}: playing on outputs 1-${layout.n}`;
  const linux = /Linux/.test(navigator.userAgent) && !/Android/.test(navigator.userAgent)
    ? " (on Linux the browser offers PipeWire's default channels: set pulse.default.position in pipewire-pulse.conf)"
    : "";
  return `${head}: folded down to stereo${linux}`;
}

/**
 * speakers(quad) / speakers(azi:[...], elev:[...]) / speakers([30, -30, ...])
 * / speakers(): the parser makes it as level()'s form (names, named args).
 */
export function handleSpeakersCue(ast) {
  const arg = type => ast?.args?.find(a => a.type === type)?.value;
  const names = [].concat(arg("names") ?? []).map(String);
  const list = v => [].concat(v ?? []).map(Number);
  let azi = arg("azi") != null ? list(arg("azi")) : null;
  if (!azi && names.length && names.every(w => Number.isFinite(Number(w)))) azi = names.map(Number);
  let res;
  if (azi) {
    const elev = list(arg("elev"));
    if (azi.some(a => !Number.isFinite(a))) res = { ok: false, text: "speakers: azi:[...] takes numbers, degrees, positive to the left" };
    else res = setSpeakers(azi.map((a, i) => [a, Number.isFinite(elev[i]) ? elev[i] : 0]));
  } else if (names.length) {
    res = setSpeakers(names[0]);
  } else {
    res = { ok: true, text: describe() };
  }
  if (res.ok && !res.folded) console.info(res.text); else console.warn(res.text);
  window.oscillaLiveLog?.(res.text, res.ok && !res.folded ? "info" : "warn");
  return null;
}

// a project starts on two speakers; the last one's layout is let go
onReset("project", function speakersHome() { setSpeakers("stereo"); });
